import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType, rootEffectsInit } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, retryWhen, switchMap, tap, withLatestFrom } from 'rxjs';

import { environment } from '@env/environment';

import { AuthService } from '@personal-area/global/authorization/services';
import { retryStrategy } from '@personal-area/global/http/services';
import { Role, User, UserOptions } from '@personal-area/global/users/models';
import { UserApiService, UserService } from '@personal-area/global/users/services';
import { LocalStorageService } from '@personal-area/storage';

import * as userActions from '../actions/user';
import { initialState } from '../reducers/user';
import { getUserStore, isUserLoaded, isUserLoading } from '../selectors/user';

const userOptionsKey = 'fbs:userOptions';

@Injectable()
export class UserEffects {
  private _store = inject(Store);
  private _actions$ = inject(Actions);
  private _authService = inject(AuthService);
  private _userApiService = inject(UserApiService);
  private _userService = inject(UserService);
  private _localStorageService = inject(LocalStorageService);

  init$ = createEffect(() =>
    this._actions$.pipe(
      ofType(rootEffectsInit),
      map(() => {
        const options = Object.assign(
          {},
          this._localStorageService.getItem<UserOptions>(userOptionsKey),
          this._authService.isLoggedIn
            ? this._userService.getPersonType(this._authService.authToken)
            : {},
        );

        return !Object.keys(options).length
          ? userActions.InitUserStore({})
          : userActions.InitUserStore({
            user: {
              ...new User(),
              role: options.role,
              country: environment.defaultLocation,
              isLoaded: false,
              isTradingPlatform: !!options.isTradingPlatform,
              isCabinetRole: !!options.isCabinetRole,
              isPartner: options.isPartner || options.role === Role.Partner,
              isInvestor: options.isInvestor || options.role === Role.Investor,
              isTrader: options.isTrader || options.role === Role.Trader,
              isExchanger:
                options.isExchanger || options.role === Role.Exchanger,
              hasOpenedAccounts: options.hasOpenedAccounts,
            },
            options: {
              ...initialState.options,
              ...options,
            },
          });
      }),
    ),
  );

  getUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType(userActions.GetUser),
      withLatestFrom(
        this._store.select(isUserLoading),
        this._store.select(isUserLoaded),
        (action, isUserLoading, isUserLoaded) =>
          isUserLoading === false && isUserLoaded === false,
      ),
      filter((flag) => flag),
      tap(() => this._store.dispatch(userActions.SetUserLoading())),
      switchMap(() =>
        this._userApiService.fetchUser(this._authService.userId, true).pipe(
          retryWhen(retryStrategy()),
          map((user) => userActions.GetUserSuccess(user)),
          catchError((error) => {

            if (!error || error.message !== 'logout') {
              this._authService.logout();
            }
            return of(userActions.GetUserFail(error));
          }),
        ),
      ),
    ),
  );

  updateUserOptions$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(userActions.UpdateUserOptions),
        withLatestFrom(
          this._store.select(getUserStore),
          ({ opts }, { options }) => ({ ...options, ...opts }),
        ),
        tap(({ location, ...rest }: UserOptions) =>
          this._localStorageService.setItem(userOptionsKey, rest),
        ),
      ),
    { dispatch: false },
  );
}
